/**
 * Static reference data for the Request/Brief step — snapshots ported verbatim
 * from the production monolith (legacy/smart-email-request/index.html). These
 * are the initial-render fallbacks; the live backend overlays them via
 * /initiatives?div=, /refdata?div=, /templates.json and /sfmc-communities.
 */

/* Division codes (monolith DIVISIONS, line 4030) + city labels (DIV_CITY, 4091). */
export const DIVISIONS = [
  "ATL", "AZH", "BAH", "BAU", "BEN", "BOI", "CHB", "CJH", "CORP", "COS", "CRS", "DAH",
  "DFW", "ETL", "FNO", "GAL", "GFC", "GVL", "HOU", "IND", "INL", "IWN", "JAX", "KAN",
  "LEH", "LRA", "LSC", "MDH", "MFL", "MIN", "NAL", "NEV", "NSH", "OCH", "OKC", "OLH",
  "PBH", "PEN", "POR", "REG", "REN", "SAN", "SDG", "SEA", "SUN", "TLA", "TPU", "TUC", "UTH",
];

const DIV_CITY: Record<string, string> = {
  CRS: "Coastal Carolinas", AZH: "Phoenix", JAX: "Jacksonville", BAU: "Bay Area Urban",
  CJH: "New Jersey-New York", LEH: "Southwest Florida", OCH: "Cal Coastal", NSH: "Nashville",
  REN: "Reno", BOI: "Boise", IND: "Indianapolis", GFC: "Alabama-Gulf Coast", OLH: "Orlando",
  INL: "Inland", REG: "Regional", GVL: "Greenville", COS: "Colorado", NEV: "Las Vegas",
  POR: "Portland", IWN: "Iowa-Nebraska", ATL: "Atlanta", PBH: "Palm Atlantic", CORP: "National",
  KAN: "Kansas", LRA: "Little Rock", CHB: "Chicago", DFW: "Dallas-Fort Worth", UTH: "Utah",
  TPU: "Tampa", SEA: "Seattle", BEN: "Bentonville-Fayetteville", PEN: "Pennsylvania-Delaware",
  SDG: "San Diego", GAL: "Charlotte", LSC: "Sacramento", NAL: "Alabama", OKC: "Oklahoma City",
  BAH: "Bay Area", TUC: "Tucson", SUN: "Raleigh-Durham", DAH: "Southeast Florida", TLA: "Tulsa",
  HOU: "Houston", SAN: "Austin-San Antonio", FNO: "Central Valley", MFL: "Mid-Florida",
  MIN: "Minnesota", MDH: "Mid-Atlantic", ETL: "Veev",
};

export function divCity(code: string): string {
  if (DIV_CITY[code]) return DIV_CITY[code];
  const m = MARKETS.find((x) => x.c === code);
  return m ? m.n : code;
}

/** [code, label] pairs for the Division Code <select> — label = "CODE — City". */
export function divisionOptions(): { code: string; label: string }[] {
  return DIVISIONS.map((c) => {
    const city = divCity(c);
    return { code: c, label: city !== c ? `${c} — ${city}` : c };
  });
}

/* Market master list (monolith MARKETS, line 4031) — the /refdata fallback. */
export const MARKETS: { c: string; n: string }[] = [
  { c: "ADM", n: "Adams County" }, { c: "AVL", n: "Asheville" }, { c: "ATL", n: "Atlanta" },
  { c: "AUS", n: "Austin / Central Texas" }, { c: "BER", n: "Berkeley County" }, { c: "BRM", n: "Birmingham" },
  { c: "BOI", n: "Boise" }, { c: "CMD", n: "Central Maryland" }, { c: "COR", n: "Central Oregon" },
  { c: "CHR", n: "Charleston" }, { c: "CHA", n: "Charlotte" }, { c: "CHO", n: "Charlottesville" },
  { c: "CHT", n: "Chattanooga" }, { c: "CHI", n: "Chicago" }, { c: "CKV", n: "Clarksville" },
  { c: "TBD", n: "College Station" }, { c: "COS", n: "Colorado Springs" }, { c: "CLM", n: "Columbia" },
  { c: "000", n: "CORP" }, { c: "CPC", n: "Corpus Christi" }, { c: "DAL", n: "Dallas / Ft. Worth" },
  { c: "DAB", n: "Daytona Beach" }, { c: "DEN", n: "Denver" }, { c: "DSM", n: "Des Moines" },
  { c: "ENC", n: "Eastern North Carolina" }, { c: "EAS", n: "Eastern Shore" }, { c: "FTS", n: "Fort Smith" },
  { c: "CCA", n: "Fresno / Bakersfield" }, { c: "FTL", n: "Ft. Lauderdale" }, { c: "GRJ", n: "Grand Junction" },
  { c: "PHI", n: "Greater Philadelphia Area" }, { c: "GRB", n: "Greensboro" }, { c: "GRN", n: "Greenville" },
  { c: "HHB", n: "Hilton-Head-Bluffton" }, { c: "HOU", n: "Houston" }, { c: "HUN", n: "Huntsville" },
  { c: "INP", n: "Indianapolis" }, { c: "INL", n: "Inland Empire" }, { c: "INW", n: "Inland Northwest" },
  { c: "JAX", n: "Jacksonville / St. Augustine" }, { c: "JFC", n: "Jefferson County" }, { c: "JON", n: "Jonesboro" },
  { c: "KCK", n: "Kansas City" }, { c: "LVE", n: "Las Vegas" }, { c: "LIT", n: "Little Rock" },
  { c: "LSA", n: "Los Angeles Metro" }, { c: "LUB", n: "Lubbock" }, { c: "MAD", n: "Madison" },
  { c: "MDC", n: "MD / D.C. Metro" }, { c: "MIA", n: "Miami" }, { c: "MID", n: "Middle Georgia" },
  { c: "MIN", n: "Minneapolis / St. Paul" }, { c: "ALC", n: "Mobile / Baldwin" }, { c: "MYB", n: "Myrtle Beach" },
  { c: "FTM", n: "Naples / Ft. Myers" }, { c: "NAS", n: "Nashville" }, { c: "NCC", n: "New Castle / Kent County" },
  { c: "CNJ", n: "New Jersey" }, { c: "NYS", n: "New York" }, { c: "NSV", n: "North Central Valley" },
  { c: "NCO", n: "Northern Colorado" }, { c: "NWA", n: "Northwest Arkansas" }, { c: "NWI", n: "Northwest Indiana" },
  { c: "OCA", n: "Ocala / Gainsville" }, { c: "OKL", n: "Oklahoma City" }, { c: "OMA", n: "Omaha" },
  { c: "ORA", n: "Orange County" }, { c: "ORL", n: "Orlando" }, { c: "PLM", n: "Palm Beach" },
  { c: "DER", n: "Palm Springs / Coachella" }, { c: "PAN", n: "Panama City" }, { c: "PEN", n: "Pensacola" },
  { c: "PHO", n: "Phoenix" }, { c: "POT", n: "Portland" }, { c: "RAL", n: "Raleigh/Durham" },
  { c: "REN", n: "Reno" }, { c: "RVA", n: "Richmond" }, { c: "RCT", n: "Rochester" },
  { c: "SAC", n: "Sacramento" }, { c: "STG", n: "Saint George" }, { c: "SLC", n: "Salt Lake City" },
  { c: "SAN", n: "San Antonio" }, { c: "SDO", n: "San Diego" }, { c: "SFR", n: "San Francisco / Bay Area" },
  { c: "SFM", n: "San Francisco Metro Area" }, { c: "BAH", n: "Santa Rosa" }, { c: "SAR", n: "Sarasota / Manatee" },
  { c: "SAV", n: "Savannah" }, { c: "SEA", n: "Seattle" }, { c: "SMD", n: "Southern Maryland" },
  { c: "SPA", n: "Space Coast / Melbourne" }, { c: "STW", n: "Stillwater" }, { c: "SUX", n: "Sussex County" },
  { c: "TAM", n: "Tampa" }, { c: "TEP", n: "Temple / Killeen" }, { c: "THC", n: "Texas Hill Country" },
  { c: "TRE", n: "Treasure Coast" }, { c: "TUC", n: "Tucson" }, { c: "TUL", n: "Tulsa" },
  { c: "TUS", n: "Tuscaloosa" }, { c: "VDC", n: "VA / D.C. Metro" }, { c: "VAN", n: "Vancouver" },
  { c: "WMD", n: "Western Maryland" }, { c: "WMV", n: "Willamette Valley" }, { c: "WIL", n: "Williamsburg" },
  { c: "WLM", n: "Wilmington" }, { c: "WSN", n: "Winston-Salem" },
];

/**
 * Market code → state backstop — ported VERBATIM from the monolith's
 * `MARKET_STATE` (index.html L10583), used by `__brandMarketInfo` (L10584–10602)
 * for exactly one job: a MARKET-level send has no community pack to derive a state
 * from, and "Atlanta" alone is not what the header says. The monolith renders
 * "Atlanta, GA".
 *
 * This is a port, not a table invented here — the codes and the states are the
 * monolith's, all 103 of them, in its own order. `useEchDyn` reads it only after
 * the community pack's `whcState` and the first homesite's `state` have both come
 * up empty, which is the monolith's own precedence (`if(!st)st=MARKET_STATE[code]`).
 * A code absent from this table renders without ", ST" rather than guessing.
 */
export const MARKET_STATE: Record<string, string> = {
  AVL: "NC", ATL: "GA", AUS: "TX", BER: "SC", BRM: "AL", BOI: "ID", CMD: "MD", COR: "OR",
  CHR: "SC", CHA: "NC", CHO: "VA", CHT: "TN", CHI: "IL", CKV: "TN", TBD: "TX", COS: "CO",
  CLM: "SC", CPC: "TX", DAL: "TX", DAB: "FL", DEN: "CO", DSM: "IA", ENC: "NC", EAS: "MD",
  FTS: "AR", CCA: "CA", FTL: "FL", GRJ: "CO", PHI: "PA", GRB: "NC", GRN: "SC", HHB: "SC",
  HOU: "TX", HUN: "AL", INP: "IN", INL: "CA", INW: "WA", JAX: "FL", JON: "AR", LVE: "NV",
  LIT: "AR", LSA: "CA", LUB: "TX", MAD: "WI", MDC: "MD", MIA: "FL", MID: "GA", MIN: "MN",
  ALC: "AL", MYB: "SC", FTM: "FL", NAS: "TN", NCC: "DE", CNJ: "NJ", NYS: "NY", NSV: "CA",
  NCO: "CO", NWA: "AR", NWI: "IN", OCA: "FL", OKL: "OK", OMA: "NE", ORA: "CA", ORL: "FL",
  PLM: "FL", DER: "CA", PAN: "FL", PEN: "FL", PHO: "AZ", POT: "OR", RAL: "NC", REN: "NV",
  RVA: "VA", SAC: "CA", STG: "UT", SLC: "UT", SAN: "TX", SDO: "CA", SFR: "CA", SFM: "CA",
  BAH: "CA", SAR: "FL", SAV: "GA", SEA: "WA", SMD: "MD", SPA: "FL", STW: "OK", SUX: "DE",
  TAM: "FL", TEP: "TX", THC: "TX", TRE: "FL", TUC: "AZ", TUL: "OK", TUS: "AL", VDC: "VA",
  VAN: "WA", WMD: "MD", WMV: "OR", WIL: "VA", WLM: "NC", WSN: "NC", ADM: "CO",
};

/* Initiative Request Focus (monolith REQ_FOCUS, line 4063). The "Community" value
   is the merged "Communities / Masterplans" option. */
export const REQ_FOCUS: { v: string; label?: string }[] = [
  { v: "Division" },
  { v: "Market" },
  { v: "Area of Interest" },
  { v: "Community", label: "Communities / Masterplans" },
];
/* Fresh-load default is "Market" — the monolith's radioList("reqFocus",…,"Market")
   (index.html L4203). Was wrongly flipped to "Community" in 7fa156e by measuring a
   side-by-side where hidden fields were counted (TAB1 audit #32, 2026-08-08). */
export const REQ_FOCUS_DEFAULT = "Market";

/** static AOI fallback — the monolith's `const AOIS` (used until /refdata answers) */
export const AOIS = ["North Atlanta", "Horizon West", "South Fulton", "Cumming / Forsyth", "Alpharetta", "Marietta"];

/* Email Template Request Type (monolith REQ_TYPE, line 4065) + show/hide rules
   (REQTYPE_RULES, lines 4265-4273). tpl = show the Nurture Template picker. */
export const REQ_TYPE: string[] = [
  "Template Email - New/Major Edits",
  "Resend Existing Division Email - Major Edits",
  "Resend Existing Division Email - Minor Edits",
  "Resend Existing Division Email - No Edits",
  "Custom Email - New",
  "3rd Party Email - New/Major Edits",
  "3rd Party Existing Email - Minor Edits",
];
export const REQ_TYPE_DEFAULT = "Template Email - New/Major Edits";
/** RESUBMIT (2026-10-13): the request type a resubmitted job carries. EMPTY on purpose until
    Digital Ops (Katie) and Katy name one — the form then keeps its default, the same as any
    new app request. Must be one of REQ_TYPE: a Resend value also switches on the Studio's
    resend caps and image lock (App.tsx), and an unknown value fails the Airtable write. */
export const RESUBMIT_REQ_TYPE = "";

export const REQTYPE_RULES: Record<string, { ref: boolean; tpl: boolean; content: boolean }> = {
  "Template Email - New/Major Edits": { ref: false, tpl: true, content: true },
  "Resend Existing Division Email - Major Edits": { ref: true, tpl: true, content: true },
  "Resend Existing Division Email - Minor Edits": { ref: true, tpl: true, content: true },
  "Resend Existing Division Email - No Edits": { ref: true, tpl: true, content: false },
  "Custom Email - New": { ref: true, tpl: false, content: true },
  "3rd Party Email - New/Major Edits": { ref: true, tpl: false, content: true },
  "3rd Party Existing Email - Minor Edits": { ref: true, tpl: false, content: false },
};
export const reqTypeRule = (v?: string) => REQTYPE_RULES[v || ""] || { ref: true, tpl: true, content: true };

/* Nurture template picker fallback (monolith NURTURE_TEMPLATES full mode, 4051). */
export const NURTURE_TEMPLATES: string[] = [
  "FTHB On-Demand Webinar", "Fast Track - Tease Coming Soon", "Tease - GO Exclusive Sneak Peek",
  "Tease - Amenities (Checkerboard)", "Launch - Grand Opening Thank You", "Sustain - Tour Your Way",
  "Sustain - Own Your Dream", "Sustain - LM HomeReady First", "Last Chance - Models Now Selling",
  "Last Chance - NSE Inventory Closeout", "Last Chance - Final Opportunity", "Event - Ribbon Cutting",
  "Event - Celebration Theme",
];
export const NURTURE_TEMPLATE_DEFAULT = "FTHB On-Demand Webinar";

/* Specialty Branding (monolith SPECIALTY, line 4126), default "No". */
/* ── TWO OPTIONS REMOVED 2026-08-31 (ATL pilot, Ashley) ───────────────────────────
   "Lennar Lifestyle" and "Lennar Investment Marketplace" are not Specialty Branding
   choices a division makes on this form — Lifestyle is an approved-community property
   and LIM is internal — so offering them here only produced wrong answers. */
export const SPECIALTY: string[] = [
  "No - Regular Lennar Branding",
  "Masterplan Specialty Branding",
  "Singular Community Specialty Branding",
];
export const SPECIALTY_DEFAULT = "No - Regular Lennar Branding";

/* Primary Message themes (monolith MESSAGES, line 4125): [code, label, isOffer].
   The monolith's select is FLAT (no optgroups), value = the CODE, option text is
   the internal legend "CODE = Label" (+ " (offer)" on rate/offer messages), and
   "CS" is pre-selected — the form stores the CODE, exactly like select.value. */
export const MESSAGES: [string, string, boolean][] = [
  ["CS", "Coming Soon", false], ["ComOv", "Community Overview", false], ["GO", "Grand Opening", false],
  ["FtFp", "Featured Floorplan", false], ["QMI", "Quick Move-in Home (No offer)", false], ["HOTW", "Home of the Week", false],
  ["OC", "Buy Now / Online Checkout", false], ["PhR", "Phase / Sales Release", false], ["FO", "Final Opportunity", false],
  ["Tour", "Tours / Appointments", false], ["SEvt", "Special Event", false], ["Web", "Webinar", false],
  ["SwGv", "Sweepstakes / Giveaways", false], ["55", "55+ / Active Adult", false], ["Ei", "Everything's Included", false],
  ["NG", "Next Gen", false], ["Ref", "Referral", false], ["SenI", "Seasonal / Holiday", false],
  ["Invstr", "Investor", false], ["CstCre", "Customer Care", false],
  ["FxdRate", "Fixed Rate", true], ["321BD", "3-2-1 Buydown Rate", true], ["21BD", "2-1 Buydown Rate", true],
  ["1BD", "1 Buydown Rate", true], ["76ARM", "7/6 ARM", true], ["CC", "Closing Costs", true],
  ["FC", "Flex Credit", true], ["MthPay", "Monthly Payment", true], ["Opts", "Options / Upgrades", true],
];
export const MESSAGE_DEFAULT = "CS"; /* monolith L4201: m[0]==="CS" ? "selected" */
const msgRow = (code: string) => MESSAGES.find((m) => m[0] === code);
/** the monolith option TEXT — what payload "Primary Message"/"Copy Input" carry */
export const messageText = (code: string): string => {
  const m = msgRow(code);
  return m ? `${m[0]} = ${m[1]}${m[2] ? " (offer)" : ""}` : code || "";
};
/** the human label after the "=" — what the AI copy engine reads (legacy __aiRead) */
export const messageHuman = (code: string): string => { const m = msgRow(code); return m ? m[1] : code || ""; };
/** the option's data-off flag */
export const messageIsOffer = (code: string): boolean => !!msgRow(code)?.[2];

/* info-tooltip copy (monolith data-tip attributes). */
export const TIP = {
  reqFocus: "Which level this send targets. Drives the recommended Salesforce report.",
  cm: "Pick one or several. For a masterplan, tap ▾ on its chip to drop specific collections (e.g., keep Granary Park but skip the 40s).",
  market: "Market code is also needed for all emails.",
  projectName: "Format: Market or Community Name, Promotion, Eblast, for Date. Example: Bay Area Summer Savings Eblast for 7/01.",
  reqType: "Minor = 2-3 changes. Major = 4+, brand new, or legal changes. If the template email has not been created yet, choose Template Email - New/Major Edits. Regional is for the Taylor Region only.",
  message: "Pick one. Defines the message theme and drives the preview and tracking.",
  deploy: "Exact date and time to schedule the send.",
  /* the Lifestyle and LIM sentences went with the options themselves (2026-08-31) */
  specialty: "Masterplan = full branding deck (also for a Collection or Community with a Masterplan). Singular Community = Community Branding Guide (a single logo does not count).",
  refFiles: "Provide the SF Name and Preview URL of the email you are referencing or resending.",
  disclaimer: "If your request has an offer, add the Disclaimer Builder request number to link the creative request.",
  anythingElse: "Any other necessary information for the project.",
};

/* ══════════════════════════════════════════════════════════════════════════════
 * WHERE "VIEW THIS REQUEST" GOES. 2026-08-31, SUN pilot.
 *
 * The done screen used to open the Duplicate A Past Job picker, which is a different
 * task: someone who has just filed a request and wants to look at it was handed a list
 * to clone from. With the record id from the submit response it deep-links to the row;
 * without one it lands on the division's queue rather than becoming a dead end.
 *
 * ON THE TABLE ID — carried over from the monolith build (20260831-134755), which is the
 * one that has actually been running, rather than re-derived here. The ids live in the
 * worker, not in this app: the only other id in the frontend is BoardView's
 * `tblfzhHRrdWeJeYK6`, and that is the Projects MIRROR table, not the legacy row the
 * submit returns an id for. If the deep link ever opens a 404 while the queue link
 * works, this constant is the thing to check first — a record id is only resolvable
 * inside the table that holds it.
 * ════════════════════════════════════════════════════════════════════════════ */
export const AT_BASE = "appzOFSAIzFpovvIl";
/* CHECKED AGAINST THE BASE 2026-08-31, not carried over from the monolith:
   `tblu1pJf0P0qTtNiF` is "Test Requests" (36 fields) and holds none of the fields this
   form writes, so that link pointed where a filed request does not exist. The intake
   table is Projects (Mirror) (177 fields) — Specialty Branding / Existing Template+
   Reference Files / Target Deployment Date-Time / Email Name / GA Tagging / Salesforce
   Lists all live there, its description names this form as what writes to it, it is what
   `buildMirror` targets, and it is what BoardView already links to. */
export const AT_REQUESTS_TABLE = "tblfzhHRrdWeJeYK6";

/** the Airtable url for a filed request, or the division's queue when there is no id */
export function requestUrl(recordId: string, divisionCode: string): string {
  const base = "https://airtable.com/" + AT_BASE + "/" + AT_REQUESTS_TABLE;
  const rec = String(recordId || "").trim();
  /* an Airtable record id is `rec` + 14 chars; anything else is not one, and appending it
     would produce a url that 404s instead of falling back to something useful */
  if (/^rec[A-Za-z0-9]{14}$/.test(rec)) return base + "/" + rec;
  const div = String(divisionCode || "").trim();
  return div ? base + "?filter_Division=" + encodeURIComponent(div) : base;
}

/* ════════════════════════════════════════════════════════════════════════════
 * THE DIVISION'S OWN QUEUE (2026-09-10, Ashley Max: "can we change the 'view requests'
 * at the end to the airtable queue for now?"; Jack: the AIM Division Interface, "for
 * each division").
 *
 * Creative Studio | AIM Division Interface › Active Projects, filtered to ONE division.
 * Airtable encodes an interface filter in the url as `?<elementId>=b:<base64 json>`, and
 * the json for this page's Division Code filter is `[0,["ayNqJ",6,[<division record
 * id>],"t8RY1"]]` — measured 2026-09-10 by setting the filter by hand and decoding the
 * url (ATL → recmenrtVLRpPudMS), then loading a constructed AZH url fresh and reading
 * "Division Code is AZH" off the page. The record id is the Divisions table's
 * (tblZNz4v5ZnvYG54S, production base) — exactly what `/refdata` returns as
 * `divisionId`. Without an id the page opens unfiltered, which is still the right
 * place, not a dead end.
 * ════════════════════════════════════════════════════════════════════════════ */
export const AIM_QUEUE_URL = "https://airtable.com/appJHKUeeWfEZNhxR/pagCnuOJ2UyuKrf1K";
export function divisionQueueUrl(divisionRecId?: string | null): string {
  const rec = String(divisionRecId || "").trim();
  if (!/^rec[A-Za-z0-9]{14}$/.test(rec)) return AIM_QUEUE_URL;
  const json = JSON.stringify([0, ["ayNqJ", 6, [rec], "t8RY1"]]);
  const b64 = (typeof btoa === "function" ? btoa(json) : Buffer.from(json).toString("base64")).replace(/=+$/, "");
  return AIM_QUEUE_URL + "?C9ZG8=" + encodeURIComponent("b:" + b64);
}
